/*
 * Build Your Bundle — Web Component.
 *
 * Scoped to its own DOM so multiple instances on one page work independently.
 * Reads a JSON payload (per-flavor tier variants/prices/availability/selling
 * plans) rendered by snippets/product-bundle-builder.liquid, then drives the
 * steppers, progress bar, purchase-option prices and add-to-cart.
 *
 * Add to cart: a single POST /cart/add.js with one line per selected flavor,
 * each at the variant matching the CURRENT total tier. Lines are grouped for
 * the cart drawer via line-item properties (see groupProperties below).
 *
 * Cart grouping scheme (verified against assets/cart-drawer.min.js, do not edit
 * that file): every line carries `_bundle: <groupId>`. The first selected
 * flavor line additionally carries `_bundle_main: true`, which the compiled
 * Svelte drawer treats as the visible "Pack" card — sibling `_bundle` lines are
 * folded in as `properties.children` (rendered "<qty> x <title>") and their
 * prices/quantities aggregated onto the main card. The drawer hides the qty
 * stepper for `_bundle` lines, so the main line's internal `quantity - 1`
 * adjustment is not displayed. We mirror this grouping on the /cart page.
 */

if (!customElements.get('bundle-builder-block')) {
  customElements.define(
    'bundle-builder-block',
    class BundleBuilderBlock extends HTMLElement {
      constructor() {
        super();
        this.state = { qty: {}, purchase: 'subscribe', planId: '' };
        this.currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
        this.moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      }

      connectedCallback() {
        try {
          // Localized strings injected by the snippet before this script runs.
          this.strings = window.pbbStrings || {};
          this.parseData();
          this.cacheDom();
          this.readDefaults();
          this.renderFlavors();
          this.bindEvents();
          this.update();
          // Reveal only once everything is rendered + priced (hidden by default
          // via the snippet's inline rule) so there is no load-time flicker.
          this.classList.add('is-ready');
        } catch (error) {
          console.error('[bundle-builder] init failed:', error);
        }
      }

      parseData() {
        const dataEl = this.querySelector('.js-pbb-data');
        if (!dataEl) throw new Error('missing data payload');
        const data = JSON.parse(dataEl.textContent);
        this.minTier = Number(data.minTier) || 6;
        this.maxTier = Number(data.maxTier) || 8;
        // Sanity-clamp: a selling-plan adjustment outside (0, 100) means the
        // value is not a usable percentage — fall back to the default 15%.
        const percent = Number(data.subscribePercent);
        this.subscribePercent = percent > 0 && percent < 100 ? percent : 15;
        this.flavors = Array.isArray(data.flavors) ? data.flavors : [];
        // index tiers by size for quick lookup
        this.flavors.forEach((flavor) => {
          flavor.tierBySize = {};
          (flavor.tiers || []).forEach((tier) => {
            flavor.tierBySize[tier.size] = tier;
          });
        });
      }

      cacheDom() {
        this.flavorList = this.querySelector('.js-pbb-flavors');
        this.tierEls = Array.from(this.querySelectorAll('.js-pbb-tier'));
        this.tierPriceEls = Array.from(this.querySelectorAll('.js-pbb-tier-price'));
        this.tierBadgeEls = Array.from(this.querySelectorAll('.js-pbb-tier-badge'));
        this.promptEl = this.querySelector('.js-pbb-prompt');
        this.purchaseFieldset = this.querySelector('.js-pbb-purchase');
        this.optionEls = Array.from(this.querySelectorAll('.js-pbb-option'));
        this.purchaseInputs = Array.from(this.querySelectorAll('.js-pbb-purchase-input'));
        this.subCompareEl = this.querySelector('.js-pbb-sub-compare');
        this.subPriceEl = this.querySelector('.js-pbb-sub-price');
        this.onetimePriceEl = this.querySelector('.js-pbb-onetime-price');
        this.chipEls = Array.from(this.querySelectorAll('.js-pbb-chip'));
        this.atcBtn = this.querySelector('.js-pbb-atc');
        this.atcLabel = this.querySelector('.js-pbb-atc-label');
        this.errorEl = this.querySelector('.js-pbb-error');
      }

      readDefaults() {
        // Subscribe is only an option when the snippet rendered its card (i.e.
        // at least one flavor carries a subscription). Otherwise force one-time.
        const subscribeAvailable = this.purchaseInputs.some((input) => input.value === 'subscribe');
        const subscribeDefault = subscribeAvailable && this.dataset.subscribeDefault !== 'false';
        this.state.purchase = subscribeDefault ? 'subscribe' : 'onetime';
        const selectedInput = this.purchaseInputs.find((input) => input.value === this.state.purchase);
        if (selectedInput) selectedInput.checked = true;
        const firstChip = this.chipEls[0];
        if (firstChip) this.state.planId = firstChip.dataset.planId || '';
      }

      bindEvents() {
        this.purchaseInputs.forEach((input) => {
          input.addEventListener('change', () => {
            if (input.checked) {
              this.state.purchase = input.value;
              this.update();
            }
          });
        });

        this.chipEls.forEach((chip) => {
          chip.addEventListener('click', () => {
            this.chipEls.forEach((other) => {
              other.classList.toggle('is-selected', other === chip);
              other.setAttribute('aria-pressed', other === chip ? 'true' : 'false');
            });
            this.state.planId = chip.dataset.planId || '';
            // Selecting a frequency implies the subscribe option.
            const subscribeInput = this.purchaseInputs.find((input) => input.value === 'subscribe');
            if (subscribeInput && !subscribeInput.checked) {
              subscribeInput.checked = true;
              this.state.purchase = 'subscribe';
            }
            this.update();
          });
        });

        this.atcBtn.addEventListener('click', () => this.addToCart());
      }

      /* ---------- helpers ---------- */

      total() {
        return Object.values(this.state.qty).reduce((sum, n) => sum + n, 0);
      }

      // Active tier = the tier whose size equals the current total, clamped to range.
      currentTierSize(total) {
        const t = typeof total === 'number' ? total : this.total();
        if (t <= this.minTier) return this.minTier;
        if (t >= this.maxTier) return this.maxTier;
        return t;
      }

      // Total price (cents) of the current selection at a hypothetical tier size.
      tierTotalAtSize(size) {
        let cents = 0;
        this.flavors.forEach((flavor) => {
          const qty = this.state.qty[flavor.id] || 0;
          if (qty <= 0) return;
          const tier = flavor.tierBySize[size];
          if (tier) cents += tier.price * qty;
        });
        return cents;
      }

      // Per-pizza price (cents) of a tier under a given selling plan. Each plan
      // carries its own allocation price, so frequencies with different discounts
      // price apart. Falls back to the tier's first plan, then to a flat percent
      // off the regular price when the variant exposes no allocations.
      planPriceForTier(tier, planId) {
        const plans = Array.isArray(tier.sellingPlans) ? tier.sellingPlans : [];
        if (plans.length) {
          const match = plans.find((plan) => String(plan.id) === String(planId)) || plans[0];
          if (match && typeof match.price === 'number') return match.price;
        }
        return Math.round(tier.price * (1 - this.subscribePercent / 100));
      }

      // Subscribe total (cents) for the current selection at a tier size + plan.
      subscribeTotalAtSize(size, planId) {
        let cents = 0;
        this.flavors.forEach((flavor) => {
          const qty = this.state.qty[flavor.id] || 0;
          if (qty <= 0) return;
          const tier = flavor.tierBySize[size];
          if (tier) cents += this.planPriceForTier(tier, planId) * qty;
        });
        return cents;
      }

      // Subscribe reference total for a full pack at a plan (mirrors tierReferenceTotal).
      subscribeReferenceTotal(size, planId) {
        const flavor = this.flavors.find((f) => f.tierBySize[size]);
        if (!flavor) return 0;
        return Math.round(this.planPriceForTier(flavor.tierBySize[size], planId) * size);
      }

      // Full-pack reference total for a tier (all <size> pizzas at the tier's
      // per-pizza rate). Static by design: the progress-bar prices never change
      // with the current selection. `tier.price` is the PER-PIZZA price.
      tierReferenceTotal(size) {
        const flavor = this.flavors.find((f) => f.tierBySize[size]);
        if (!flavor) return 0;
        return Math.round(flavor.tierBySize[size].price * size);
      }

      // Savings (cents) of a full tier vs paying the minimum-tier per-pizza rate
      // for the same number of pizzas. Computed from prices, never hardcoded.
      tierSavings(size) {
        const flavor = this.flavors.find((f) => f.tierBySize[size] && f.tierBySize[this.minTier]);
        if (!flavor) return 0;
        const baseRate = flavor.tierBySize[this.minTier].price; // per-pizza at min tier
        const tierRate = flavor.tierBySize[size].price; // per-pizza at this tier
        const perPizzaSaving = baseRate - tierRate;
        if (perPizzaSaving <= 0) return 0;
        return Math.round(perPizzaSaving * size);
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, this.moneyFormat);
        }
        return `$${(cents / 100).toFixed(2)}`;
      }

      flavorAvailableAtCurrentTier(flavor) {
        const tier = flavor.tierBySize[this.currentTierSize()];
        return Boolean(flavor.available && tier && tier.available);
      }

      flavorMaxCap(flavor) {
        const tier = flavor.tierBySize[this.currentTierSize()];
        if (tier && typeof tier.inventory === 'number') return tier.inventory;
        return Infinity;
      }

      /* ---------- render ---------- */

      renderFlavors() {
        const fragment = document.createDocumentFragment();
        this.flavorRows = {};

        this.flavors.forEach((flavor) => {
          const li = document.createElement('li');
          li.className = 'pbb__flavor';
          li.dataset.flavorId = flavor.id;

          const badge = flavor.badge
            ? `<span class="pbb__flavor-badge">${this.escapeHtml(flavor.badge)}</span>`
            : '';

          // Decorative: the flavor title is shown as adjacent text, so an empty
          // alt avoids screen readers announcing the name twice.
          const image = flavor.image
            ? `<img class="pbb__flavor-image" src="${this.escapeHtml(flavor.image)}" alt="" width="96" height="96" loading="lazy">`
            : '<span class="pbb__flavor-image" aria-hidden="true"></span>';

          li.innerHTML = [
            badge,
            image,
            '<div class="pbb__flavor-body">',
            `<p class="pbb__flavor-title">${this.escapeHtml(flavor.title)}</p>`,
            `<p class="pbb__flavor-desc">${this.escapeHtml(flavor.description || '')}</p>`,
            '</div>',
            '<div class="pbb__flavor-action js-pbb-action"></div>',
          ].join('');

          this.flavorRows[flavor.id] = li;
          fragment.appendChild(li);
        });

        this.flavorList.innerHTML = '';
        this.flavorList.appendChild(fragment);
      }

      // 20x20 plus/minus icons matching the Figma Icon button.
      stepIcon(type) {
        const path =
          type === 'minus'
            ? '<path d="M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'
            : '<path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>';
        return `<svg class="pbb__step-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">${path}</svg>`;
      }

      // Build the markup for a given mode once; dynamic bits are updated separately
      // so CSS transitions (hover, disabled dimming) are not reset every update.
      buildAction(mode, flavor) {
        if (mode === 'oos') {
          return `<span class="pbb__flavor-oos-label">${this.escapeHtml(this.strings.outOfStock || '')}</span>`;
        }
        const title = flavor.title || '';
        if (mode === 'add') {
          // The visible label is just "Add"; the accessible name names the flavor.
          const addLabel = this.escapeHtml(`${this.strings.add || ''} ${title}`.trim());
          return `
            <button type="button" class="pbb__add js-pbb-add" aria-label="${addLabel}">
              <span aria-hidden="true">${this.escapeHtml(this.strings.add || '')}</span>
              <svg class="pbb__add-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>`;
        }
        const decLabel = this.escapeHtml((this.strings.removeOne || '').replace('__title__', title).trim());
        const incLabel = this.escapeHtml((this.strings.addOne || '').replace('__title__', title).trim());
        return `
          <div class="pbb__stepper" role="group" aria-label="${this.escapeHtml(title)}">
            <button type="button" class="pbb__step-btn js-pbb-dec" aria-label="${decLabel}">${this.stepIcon('minus')}</button>
            <span class="pbb__step-count js-pbb-count" aria-live="polite" aria-atomic="true">0</span>
            <button type="button" class="pbb__step-btn js-pbb-inc" aria-label="${incLabel}">${this.stepIcon('plus')}</button>
          </div>`;
      }

      renderAction(flavor) {
        const row = this.flavorRows[flavor.id];
        const actionEl = row.querySelector('.js-pbb-action');
        const qty = this.state.qty[flavor.id] || 0;
        const total = this.total();
        const atMax = total >= this.maxTier;
        const atCap = qty >= this.flavorMaxCap(flavor);
        const oos = !this.flavorAvailableAtCurrentTier(flavor);

        row.classList.toggle('is-oos', oos && qty === 0);
        row.classList.toggle('pbb__flavor--selected', qty > 0);

        let mode = 'add';
        if (oos && qty === 0) mode = 'oos';
        else if (qty > 0) mode = 'stepper';

        // Rebuild markup only when the mode changes (preserves transitions).
        if (actionEl.dataset.mode !== mode) {
          const isInitial = !actionEl.dataset.mode;
          actionEl.dataset.mode = mode;
          actionEl.innerHTML = this.buildAction(mode, flavor);
          const addBtn = actionEl.querySelector('.js-pbb-add');
          if (addBtn) addBtn.addEventListener('click', () => this.changeQty(flavor.id, 1));
          const decBtn = actionEl.querySelector('.js-pbb-dec');
          if (decBtn) decBtn.addEventListener('click', () => this.changeQty(flavor.id, -1));
          const incBtn = actionEl.querySelector('.js-pbb-inc');
          if (incBtn) incBtn.addEventListener('click', () => this.changeQty(flavor.id, 1));

          // Fade the new control in (skip on first paint to avoid a flash).
          if (!isInitial) {
            actionEl.style.opacity = '0';
            window.requestAnimationFrame(() => {
              actionEl.style.opacity = '1';
            });
          }
        }

        // Update dynamic bits without rebuilding (keeps transitions smooth).
        if (mode === 'add') {
          // At max bundle size, a not-yet-added flavor can't be added: dim + lock.
          actionEl.querySelector('.js-pbb-add').disabled = atMax;
        } else if (mode === 'stepper') {
          actionEl.querySelector('.js-pbb-count').textContent = qty;
          actionEl.querySelector('.js-pbb-inc').disabled = atMax || atCap;
          // Minus stays enabled — removing is always allowed.
        }
      }

      changeQty(flavorId, delta) {
        const current = this.state.qty[flavorId] || 0;
        let next = current + delta;
        if (next < 0) next = 0;

        if (delta > 0) {
          if (this.total() >= this.maxTier) return; // hard cap at max tier
          const flavor = this.flavors.find((f) => f.id === flavorId);
          if (flavor && next > this.flavorMaxCap(flavor)) return; // inventory cap
        }

        if (next === 0) delete this.state.qty[flavorId];
        else this.state.qty[flavorId] = next;

        this.update();
      }

      update() {
        const total = this.total();
        const tierSize = this.currentTierSize(total);

        // Progress bar: prices, badges, fill, active/complete markers.
        this.tierPriceEls.forEach((el) => {
          const size = Number(el.dataset.tier);
          el.textContent = this.formatMoney(this.tierReferenceTotal(size));
        });

        this.tierBadgeEls.forEach((badgeEl, idx) => {
          const size = this.minTier + idx;
          const savings = this.tierSavings(size);
          if (savings > 0 && this.strings.saveBadge) {
            badgeEl.textContent = this.strings.saveBadge.replace('__amount__', this.formatMoney(savings));
            badgeEl.hidden = false;
          } else {
            badgeEl.hidden = true;
          }
        });

        this.tierEls.forEach((tierEl) => {
          const size = Number(tierEl.dataset.tier);
          tierEl.classList.toggle('is-active', total >= this.minTier && size === tierSize);
          tierEl.classList.toggle('is-complete', total >= size);
          // Continuous line: the segment entering a dot fills once that dot is
          // reached; the segment leaving it fills once the next dot is reached.
          const leftLine = tierEl.querySelector('.js-pbb-line-left');
          const rightLine = tierEl.querySelector('.js-pbb-line-right');
          if (leftLine) leftLine.classList.toggle('is-filled', total >= size);
          if (rightLine) rightLine.classList.toggle('is-filled', total >= size + 1);
        });

        // Re-render flavor actions (steppers / add / oos).
        this.flavors.forEach((flavor) => this.renderAction(flavor));

        this.updatePurchaseOptions(total, tierSize);
        this.updatePrompt(total);
        this.updateAtc(total);
      }

      updatePurchaseOptions(total, tierSize) {
        const hasSelection = total >= this.minTier;
        const baseTotal = hasSelection ? this.tierTotalAtSize(tierSize) : this.tierReferenceTotal(this.minTier);
        // Subscribe price follows the selected frequency chip's plan (plans can
        // carry different discounts), not a single flat percent.
        const subTotal = hasSelection
          ? this.subscribeTotalAtSize(tierSize, this.state.planId)
          : this.subscribeReferenceTotal(this.minTier, this.state.planId);

        if (this.subCompareEl) this.subCompareEl.textContent = this.formatMoney(baseTotal);
        if (this.subPriceEl) this.subPriceEl.textContent = this.formatMoney(subTotal);
        if (this.onetimePriceEl) this.onetimePriceEl.textContent = this.formatMoney(baseTotal);

        this.optionEls.forEach((optionEl) => {
          optionEl.classList.toggle('is-selected', optionEl.dataset.option === this.state.purchase);
        });
        if (this.purchaseFieldset) {
          this.purchaseFieldset.classList.toggle('is-onetime', this.state.purchase === 'onetime');
        }
      }

      updatePrompt(total) {
        if (!this.promptEl || !this.strings.promptLead) {
          if (this.promptEl) this.promptEl.hidden = true;
          return;
        }
        if (total < this.minTier) {
          const remaining = this.minTier - total;
          this.promptEl.innerHTML = `<strong>${this.strings.promptLead.replace(
            '__count__',
            remaining
          )}</strong> ${(this.strings.promptRest || '').replace('__count__', this.minTier)}`;
          this.promptEl.hidden = false;
        } else if (total >= this.maxTier) {
          this.promptEl.innerHTML = `<strong>${(this.strings.maxReached || '').replace(
            '__count__',
            this.maxTier
          )}</strong>`;
          this.promptEl.hidden = false;
        } else {
          this.promptEl.hidden = true;
        }
      }

      updateAtc(total) {
        const enabled = total >= this.minTier;
        this.atcBtn.disabled = !enabled;
        this.atcBtn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      }

      /* ---------- cart ---------- */

      async addToCart() {
        const total = this.total();
        if (total < this.minTier) return;

        this.setError('');
        this.atcBtn.classList.add('is-loading');
        this.atcBtn.disabled = true;
        if (this.strings.adding) this.atcLabel.textContent = this.strings.adding;

        const tierSize = this.currentTierSize(total);
        const groupId = this.generateGroupId();
        const subscribe = this.state.purchase === 'subscribe';
        const bundleTitle = this.dataset.bundleTitle || '';
        const bundleImage = this.dataset.bundleImage || '';

        const items = [];
        this.flavors.forEach((flavor) => {
          const qty = this.state.qty[flavor.id] || 0;
          if (qty <= 0) return;
          const tier = flavor.tierBySize[tierSize];
          if (!tier) return;

          const properties = { _bundle: groupId };
          // Parent product title / image — used as the grouped "Pack" card in the cart.
          if (bundleTitle) properties._bundle_title = bundleTitle;
          if (bundleImage) properties._bundle_image = bundleImage;
          if (items.length === 0) properties._bundle_main = true;

          const line = { id: tier.variantId, quantity: qty, properties };
          if (subscribe) {
            // Use the frequency picked via the "Deliver every" chips when the
            // variant allocates that plan; fall back to its first allocation.
            const planIds = Array.isArray(tier.sellingPlanIds) ? tier.sellingPlanIds : [];
            const chosenPlan = this.state.planId && planIds.includes(Number(this.state.planId))
              ? Number(this.state.planId)
              : planIds[0];
            if (chosenPlan) line.selling_plan = chosenPlan;
          }
          items.push(line);
        });

        if (items.length === 0) {
          this.resetAtc();
          this.setError((this.strings.errorSelect || '').replace('__count__', this.minTier));
          return;
        }

        try {
          const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });

          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            const serverMessage = payload.description || payload.message;
            const requestError = new Error(serverMessage || 'cart add failed');
            // Shopify's description (e.g. "Out of stock") is customer-facing —
            // surface it instead of the generic error message.
            requestError.isCustomerFacing = Boolean(serverMessage);
            throw requestError;
          }

          // Reuse the theme's drawer: update-cart refetches + opens it.
          document.dispatchEvent(new CustomEvent('update-cart'));
          document.dispatchEvent(new CustomEvent('toggle-cart'));

          this.resetSelection();
        } catch (error) {
          console.error('[bundle-builder] add to cart failed:', error);
          const message = error.isCustomerFacing ? error.message : this.strings.errorGeneric;
          this.setError(message || '');
        } finally {
          this.resetAtc();
        }
      }

      resetSelection() {
        this.state.qty = {};
        this.update();
      }

      resetAtc() {
        this.atcBtn.classList.remove('is-loading');
        if (this.strings.addToCart) this.atcLabel.textContent = this.strings.addToCart;
        this.updateAtc(this.total());
      }

      setError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = !message;
      }

      generateGroupId() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
          return `pbb-${window.crypto.randomUUID()}`;
        }
        return `pbb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }

      escapeHtml(value) {
        return String(value == null ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }
    }
  );
}
